import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { metaAdsGradientBorderOnlyClass } from "./metaAdsTheme";

/**
 * Hero illustration for Meta Ads hub — “reach / radar” metaphor, finished frame
 * matching dashboard grays (not flat black).
 */
const MetaAdsHubHeroVisual = ({ className }) => {
  const { t } = useTranslation("metaAds");
  const uid = useId();
  const gid = (s) => `${uid}-${s}`;

  return (
    <div
      className={cn(
        "relative mx-auto w-full max-w-[min(100%,340px)]",
        className,
      )}
    >
      {/* Same 1px cyan→purple frame as Connect card (border only, no extra drop shadow) */}
      <div className={metaAdsGradientBorderOnlyClass}>
        <div
          className={cn(
            "relative overflow-hidden rounded-2xl",
            "bg-gradient-to-b from-gray-800/60 via-gray-900/80 to-gray-950/90",
            "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_12px_40px_-12px_rgba(0,0,0,0.5)]",
          )}
        >
          {/* Ambient light — depth without fighting gray-900 card */}
          <div
            className="bg-primary/10 pointer-events-none absolute -top-1/4 left-1/2 h-[70%] w-[90%] -translate-x-1/2 rounded-full blur-3xl"
            aria-hidden
          />
          <div
            className="bg-brand-cyan/10 pointer-events-none absolute right-0 bottom-0 h-1/2 w-1/2 rounded-full blur-2xl"
            aria-hidden
          />

          <div className="relative px-5 pt-6 pb-4 sm:px-7 sm:pt-8 sm:pb-5">
            <div className="relative mx-auto aspect-square max-h-[260px] w-full max-w-[260px]">
              <svg
                viewBox="0 0 200 200"
                className="h-full w-full"
                role="img"
                aria-label={t("hub.visualAriaLabel")}
              >
                <defs>
                  <linearGradient
                    id={gid("arc")}
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop
                      offset="0%"
                      stopColor="var(--brand-cyan)"
                      stopOpacity="1"
                    />
                    <stop
                      offset="55%"
                      stopColor="var(--brand-purple)"
                      stopOpacity="0.95"
                    />
                    <stop
                      offset="100%"
                      stopColor="var(--primary)"
                      stopOpacity="0.75"
                    />
                  </linearGradient>
                  <radialGradient id={gid("core")} cx="50%" cy="45%" r="55%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.06)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                  </radialGradient>
                  <filter
                    id={gid("glow")}
                    x="-40%"
                    y="-40%"
                    width="180%"
                    height="180%"
                  >
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* Soft core wash */}
                <circle
                  cx="100"
                  cy="100"
                  r="96"
                  fill={`url(#${gid("core")})`}
                />

                {/* Concentric radar rings */}
                {[92, 76, 60, 44, 28].map((r, i) => (
                  <circle
                    key={r}
                    cx="100"
                    cy="100"
                    r={r}
                    fill="none"
                    stroke="rgba(148,163,184,0.14)"
                    strokeWidth={i === 0 ? 1.25 : 1}
                    opacity={0.35 + i * 0.1}
                  />
                ))}

                {/* Dashed scan ring — slow rotation (see App.css) */}
                <g
                  transform="translate(100 100)"
                  className="text-brand-cyan/25"
                >
                  <circle
                    r="84"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeDasharray="4 10"
                    className="meta-ads-hub-radar"
                  />
                </g>

                {/* Base track for arc */}
                <circle
                  cx="100"
                  cy="100"
                  r="72"
                  fill="none"
                  stroke="rgba(148,163,184,0.12)"
                  strokeWidth="14"
                />

                {/* Primary data arc */}
                <path
                  d="M 100 28 A 72 72 0 1 1 36 124"
                  fill="none"
                  stroke={`url(#${gid("arc")})`}
                  strokeWidth="14"
                  strokeLinecap="round"
                  filter={`url(#${gid("glow")})`}
                />

                {/* Inner focus ring */}
                <circle
                  cx="100"
                  cy="100"
                  r="22"
                  fill="rgba(15,23,42,0.65)"
                  stroke="rgba(148,163,184,0.25)"
                  strokeWidth="1"
                />
                <g className="text-brand-cyan/40">
                  <circle
                    cx="100"
                    cy="100"
                    r="8"
                    fill="currentColor"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="1"
                  />
                </g>

                {/* Cardinal ticks — “instrument” finish */}
                <g stroke="rgba(148,163,184,0.35)" strokeWidth="1.5">
                  <line x1="100" y1="12" x2="100" y2="22" />
                  <line x1="100" y1="178" x2="100" y2="188" />
                  <line x1="12" y1="100" x2="22" y2="100" />
                  <line x1="178" y1="100" x2="188" y2="100" />
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaAdsHubHeroVisual;
